import {useSession} from 'next-auth/react';
import React, {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {useHistory} from 'react-router-dom';
import RootStore from '../Core/mobx/RootStore';
import DealSaleToastContainer from '../Leaderboard/DealSaleToast';
import type {DealSaleEvent, DealSaleToast} from '../Leaderboard/data';
import {ConnectionStatus, MQTTConnection} from '../lib/mqtt-connection';

type RealtimeContextType = {
    client: MQTTConnection | null;
    connectionStatus: ConnectionStatus;
    subscriptionStatus: 'connected' | 'disconnected' | 'connecting';
};

type User = {
    name: string;
    email: string;
    image: string;
};

type Session = {
    data: {
        user: User;
        expires: string;
        id_token: string;
        roles: Array<'DEVELOPER' | 'AGENT' | 'MANAGER' | 'AP_MANAGER'>;
    };
    status: 'authenticated';
};

enum HookEvent {
    CALL_ANSWERED = 'CALL_ANSWERED',
    CALL_ENDED = 'CALL_ENDED',
}

enum Origin {
    KIXIE = 'KIXIE',
}

export type NormalizedWebhookData = {
    origin: Origin;
    clientEmail: string;
    agentEmail: string;
    clientPhoneNumber: string;
    answerDate: string;
    hookEvent: HookEvent;
};

export type CallEventType = {
    origin: string;
    answerDate: string;
    agentEmail: string;
    callId: string;
    clientEmail: string;
    clientPhoneNumber: string;
    crmLink: string;
    hubspotData: Record<string, {value: string}>;
    hookEvent: 'CALL_ANSWERED' | 'CALL_ENDED' | 'CALL_DIALED';
    walkthroughData: Record<string, unknown>;
    walkthroughState: Record<string, unknown>;
    persona?: string;
};

type RealtimeEvent = {
    properties: CallEventType;
};
export const RealtimeContext = createContext<RealtimeContextType | undefined>(undefined);

type GlobalSubscription = {
    topic: string;
    handler: (topic: string, payload: any) => void;
};

export const RealtimeProvider = ({children}: {children: React.ReactNode}) => {
    const session = useSession() as unknown as Session;
    const history = useHistory();

    const [client, setClient] = useState<MQTTConnection | null>(null);
    const clientRef = useRef<MQTTConnection | null>(null);
    const initialTokenRef = useRef<string | null>(null);
    const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('disconnected');
    const [subscriptionStatus, setSubscriptionStatus] = useState<'connected' | 'disconnected' | 'connecting'>(
        'disconnected',
    );
    const [dealSaleToasts, setDealSaleToasts] = useState<DealSaleToast[]>([]);

    const MAX_TOASTS = 3;

    const addDealSaleToast = useCallback((event: DealSaleEvent) => {
        const toast: DealSaleToast = {
            ...event,
            id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        };

        setDealSaleToasts((prev) => {
            const next = [toast, ...prev];
            return next.slice(0, MAX_TOASTS);
        });
    }, []);

    const removeDealSaleToast = useCallback((id: string) => {
        setDealSaleToasts((prev) => prev.filter((t) => t.id !== id));
    }, []);

    // Define global subscriptions - these will be active as soon as connection is established
    const getGlobalSubscriptions = useCallback((): GlobalSubscription[] => {
        if (!session?.data?.user?.email) return [];

        const encodedEmail = Buffer.from(session.data.user.email).toString('hex');
        return [
            {
                topic: `platform-api-athena/${process.env.NEXT_PUBLIC_ATHENA_STAGE_NAME}/${encodedEmail}/live-tab`,
                handler: (_topic: string, payload: RealtimeEvent) => {
                    if (payload.properties.hookEvent === 'CALL_DIALED')
                        RootStore.uiStore.showSnackbar('Dialing. View call details.', 'info', 10000, {
                            label: 'View Call',
                            onClick: () => history.push(`/live-call`),
                        });
                    if (payload.properties.hookEvent === 'CALL_ANSWERED')
                        RootStore.uiStore.showSnackbar('Call connected. View call details.', 'success', 10000, {
                            label: 'View Call',
                            onClick: () => history.push(`/live-call`),
                        });

                    RootStore.liveTabStore.addMessage(payload.properties);
                },
            },
            // Add other global subscriptions here as needed
            {
                topic: `platform-api-athena/${process.env.NEXT_PUBLIC_ATHENA_STAGE_NAME}/all/leaderboard-deal-sale`,
                handler: (_topic: string, payload: DealSaleEvent) => {
                    addDealSaleToast(payload);
                },
            },
        ];
    }, [session?.data?.user?.email, addDealSaleToast]);

    // Handle initial connection and global subscriptions
    useEffect(() => {
        let aborted = false;

        const mqttClient = new MQTTConnection(
            process.env.NEXT_PUBLIC_MQTT_ENDPOINT_URL as string,
            session.data.id_token,
            setConnectionStatus,
        );

        clientRef.current = mqttClient;
        initialTokenRef.current = session.data.id_token;

        const setupConnection = async () => {
            try {
                await mqttClient.connect(session.data.id_token, session.data.user.email);
                if (aborted) return;

                setClient(mqttClient);

                // Set up global subscriptions
                const globalSubs = getGlobalSubscriptions();
                setSubscriptionStatus('connecting');
                for (const sub of globalSubs) {
                    if (aborted) return;
                    try {
                        await mqttClient.subscribe(sub.topic, sub.handler);
                    } catch (error) {
                        console.error(`Failed to set up global subscription for ${sub.topic}:`, error);
                    }
                }

                if (aborted) return;
                setSubscriptionStatus('connected');
            } catch (error) {
                if (aborted) return;
                console.error('MQTT connection failed:', error);
                setSubscriptionStatus('disconnected');
            }
        };

        if (session?.data?.user?.email) {
            setupConnection();
        }

        return () => {
            aborted = true;
            clientRef.current = null;
            mqttClient.destroy();
        };
    }, [session?.data?.user?.email]);

    // Proactive reconnect when token refreshes (skip initial token)
    useEffect(() => {
        if (clientRef.current && session.data.id_token) {
            // Skip the first invocation -- the initial connect already used this token
            if (initialTokenRef.current === session.data.id_token) {
                initialTokenRef.current = null;
                return;
            }
            clientRef.current.reconnect(session.data.id_token);
        }
    }, [session.data.id_token]);

    const contextValue = useMemo(
        () => ({
            client,
            connectionStatus,
            subscriptionStatus,
        }),
        [client, connectionStatus, subscriptionStatus],
    );

    return (
        <RealtimeContext.Provider value={contextValue}>
            {children}
            <DealSaleToastContainer toasts={dealSaleToasts} onRemove={removeDealSaleToast} />
        </RealtimeContext.Provider>
    );
};

export const useRealtime = (): RealtimeContextType => {
    const context = useContext(RealtimeContext);
    if (!context) {
        throw new Error('useMQTT must be used within an MQTTProvider');
    }
    return context;
};
